import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useRecoilState } from "recoil";
import { omitBy, isNil, map, isEmpty, isArray } from "lodash";
import moment from "moment";
import { orderAtom } from "Recoil/order.atom";
import useSession from "Hooks/session.hook";
import { useQuery } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";
import { Button, Tabs, BasicModal } from "@life-line-lab/components-react";
import { RightCircleOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
import { fetchCustomerList } from "Services/customer.service";
import {
  fetchCustomerLocationList,
  fetchCustomerLocation,
} from "Services/location.service";
import {
  fetchOrderList,
  updateOrderStatus,
  fetchOrderStatisticsCount,
  getAirwayBills,
} from "Services/order.service";
import { listServicePoints } from "Services/delivery-settings.services";
import { createPickUp, createDropOff } from "Services/delivery.services";
import { SortingMode } from "Types/common.types";
import { toaster, toNum } from "Helpers/index";
import { CustomerApprovalStatus } from "Types/customer-form.types";
import "./parcels-order.scss";
import StatisticBox from "Components/shared/statistic-box";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import OrderTab from "Components/order-management/parcel-order-tabs/order-tab";
import PendingConfirmationTab from "Components/order-management/parcel-order-tabs/pending-confirmation-tab";
import PendingReviewTab from "Components/order-management/parcel-order-tabs/pending-review-tab";
import DispatchReadyTab from "Components/order-management/parcel-order-tabs/dispatch-ready-tab";
import EnrouteTab from "Components/order-management/parcel-order-tabs/enroute-tab";
import RejectedOrdersTab from "Components/order-management/parcel-order-tabs/rejected-orders-tab";
import CancelledOrdersTab from "Components/order-management/parcel-order-tabs/cancelled-orders-tab";
import { tabStatus } from "Data/order";
import useAccessControlList from "Hooks/access-control.hook";
import FlagRejectParcelModal from "Components/order-management/shared/parcel-modals/flag-reject-parcel";
import ConfirmModal from "Components/order-management/shared/parcel-modals/confirm-modal";
import { routeToOrderView } from "Helpers/route-order-view";
import { useArchiveOrders } from "Hooks/archive-orders.hook";
import {
  useOrderListingStates,
  OrderListingProvider,
} from "Helpers/state-providers/order-listing-states";
import useCorporateType from "Hooks/corporate-type.hook";

const ConfirmRejectModal: React.FC<{
  callModal: React.Dispatch<React.SetStateAction<any>>;
  modal: any;
  handleResetModalState: () => void;
}> = ({ callModal, modal, handleResetModalState }) => {
  return (
    <BasicModal
      isOpen={modal.open}
      isClosable={true}
      isMaskClosable={false}
      onClose={handleResetModalState}
    >
      {callModal}
    </BasicModal>
  );
};

const ParcelsOrderInner = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const {
    setAllOrderList,
    currentListCount,
    setCurrentListCount,
    setCustomersList,
    setLocationList,
    setDropOffLocationList,
    setWorkingHours,
    selectedRows,
    setSelectedRows,
    setIsLoading,
    filterObj,
    setFilterObj,
    globalFilterState,
    initialFilters
  } = useOrderListingStates();
  const { paginationComponent, itemPerPage, queryParams } = usePagination({
    currentListCount,
  });
  const tabKey = query.get("tabKey") || "0";
  const page: any = query.get("page") || 1;
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const { org, roleTypes } = useSession();
  const { isCustomer } = useCorporateType();
  const [orderState, setOrderState] = useRecoilState<any>(orderAtom);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Order_Management",
  });

  const initialModal: any = {
    open: false,
  };

  const [callModal, setCallModal] = useState<any>(<></>);
  const [modal, setModal] = useState<any>(initialModal);
  const showCreateOption = permission.rights.includes("CREATE");

  const [parcelStatistics, setParcelStatistics] = useState<any>([
    {
      key: "open",
      name: "newOrders",
      tab: "0",
      count: 0,
      iconSrc: "/assets/icons/newOrder.svg",
    },
    {
      key: "pending",
      name: "pending",
      tab: "2",
      count: 0,
      iconSrc: "/assets/icons/pending.svg",
    },
    {
      key: "dispatchReady",
      name: "dispatchReady",
      tab: "3",
      count: 0,
      iconSrc: "/assets/icons/dispatchReady.svg",
    },
    {
      key: "enroute",
      name: "enroute",
      tab: "4",
      count: 0,
      iconSrc: "/assets/icons/midmile.svg",
    },
  ]);

  const handleFetchServicePoints = async (filters?: any) => {
    setIsLoading && setIsLoading(true);

    const responseData = await listServicePoints(omitBy(filters, isNil));
    if (responseData && !responseData.error) {
      setDropOffLocationList(responseData);
    } else {
      toaster("error", "Failed to fetch service points.");
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchCustomers = async () => {
    if (!isCustomer) {
      setIsLoading && setIsLoading(true);

      let orgID = "";
      orgID =
        globalFilterState.selectedOrgIDs?.length &&
        globalFilterState.selectedOrgIDs?.length <= 1
          ? globalFilterState.selectedOrgIDs[0]
          : org._id;
      const responseData = await fetchCustomerList(
        {},
        orgID,
        CustomerApprovalStatus.APPROVED,
      );

      if (responseData && !responseData.error) {
        setCustomersList(responseData);
      } else {
        if (typeof responseData.error?.message === "string") {
          toaster("error", responseData.error.message);
        } else {
          toaster("error", responseData.error.message.message.replace('"', ""));
        }
      }

      setIsLoading && setIsLoading(false);
    }
  };

  const fetchOrders = async () => {
    const getPayloadFilters = (selectionFilter: any) => {
      const removeAncestorOrgIDs = (filters: any): any => {
        if (filters.orgID) {
          return { ...filters, ancestorOrgIDs: undefined };
        }
        return filters;
      };
    
      const assignCustomerToOrgID = (filters: any): any => {
        return {
          ...filters,
          orgID: filters.customersList
            ? [filters.customersList]
            : initialFilters?.orgID,
        };
      };
    
      const formatDeliveryDate = (filters: any): any => {
        return {
          ...filters,
          deliveryDate: filters.deliveryDate
            ? moment(filters.deliveryDate).startOf("day")
            : null,
        };
      };
    
      const removeCustomersList = (filters: any): any => {
        const updatedFilter = {...filters};
        delete updatedFilter.customersList;
        return updatedFilter
      };
    
      const removeEmptyAndNullValues = (filters: any): any => {
        return omitBy(filters, (value) => isNil(value) || value === '');
      };
    
      const filterWithNoAncestor = removeAncestorOrgIDs(selectionFilter);
      const filterCustomerToOrg = assignCustomerToOrgID(filterWithNoAncestor);
      const filterFormattedDate = formatDeliveryDate(filterCustomerToOrg);
      const filterWithNoEmptyNull = removeEmptyAndNullValues(filterFormattedDate);
      const finalFilters = removeCustomersList(filterWithNoEmptyNull);
    
      return finalFilters;
    };

    setIsLoading && setIsLoading(true);

    setOrderState({
      ...orderState,
      status: { ...orderState.status, fetch: "loading" },
    });

    const responseData =
    await fetchOrderList(
      getPayloadFilters(filterObj),
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "createdAt", mode: "DESC" },
    );

    if (!isNil(responseData) && isArray(responseData)) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "success" },
        list: responseData,
      });
      setAllOrderList(responseData);
      setCurrentListCount(responseData.length);
    } else {
      setAllOrderList([]);
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "error" },
      });
    }

    setIsLoading && setIsLoading(false);
  };

  const { allArchiveObjects } = useArchiveOrders({
    fetchOrders,
    filterObj,
  });

  const {
    archiveStateObject,
    handleListArchivedOrders,
    handleRevertArchivedOrders,
  } = allArchiveObjects;

  const { isArchive, setIsArchive } = archiveStateObject;

  const handleRefreshData = () => {
    isArchive ? handleListArchivedOrders() : fetchOrders();
    getStatCount();
  };

  const handleParcelStatus = (
    selectedOrderID: string[],
    action: string,
    issueType?: any,
  ) => {
    const { issueType: selectedIssueType, remarks: selectedIssueRemark } =
      issueType || {};
    const issueTypes = selectedIssueType ? [selectedIssueType] : undefined;
    const issueRemark = selectedIssueRemark || undefined;

    let status = "";

    switch (action) {
      case "REJECT":
        status = "REJECTED";
        break;
      case "FLAG":
        status = "PENDING_CONFIRMATION";
        break;
      case "CONFIRM":
      case "APPROVE":
        status = "DISPATCH_READY";
        break;
      case "CANCEL":
        status = "CANCELLED";
        break;
      default:
        status = "";
    }

    action === "REVERT"
      ? handleRevertArchivedOrders(selectedOrderID)
      : handleStatusUpdate(status, selectedOrderID, issueTypes, issueRemark);

    setModal(initialModal);
  };

  const handleModalAction = (
    action: string,
    selectedOrderID: string[],
    orderID?: string,
    customer?: string,
  ) => {
    switch (action) {
      case "REJECT":
      case "FLAG":
      case "CANCEL":
        setCallModal(
          <FlagRejectParcelModal
            action={action}
            orderID={orderID}
            customer={customer}
            onSubmit={(issueType: any) => {
              handleParcelStatus(selectedOrderID, action, issueType);
            }}
            onCancel={handleResetModalState}
          />,
        );
        break;
      case "REVERT":
      case "APPROVE":
      case "CONFIRM":
        setCallModal(
          <ConfirmModal
            onSubmit={() => {
              handleParcelStatus(selectedOrderID, action);
            }}
            onCancel={handleResetModalState}
            contentWord={t("txt.confirmParcelQuestion")}
          />,
        );
        break;
      case "MASSCONFIRM":
        setCallModal(
          <ConfirmModal
            onSubmit={() => {
              const allOrderIDs = selectedRows?.flatMap((order) => [
                order?._id,
                ...(order?.childrenOrderIDs || []),
              ]);

              handleStatusUpdate("DISPATCH_READY", allOrderIDs);
              handleResetModalState();
            }}
            onCancel={handleResetModalState}
            contentWord={t("txt.massConfirmParcelQuestion")}
          />,
        );
        break;
    }

    setModal({ open: true });
  };

  const getStatCount = async () => {
    let orgIDs: any[] = [];
    orgIDs = globalFilterState.selectedOrgIDs?.length
      ? globalFilterState.selectedOrgIDs
      : [org._id];
    const response = await fetchOrderStatisticsCount(orgIDs);
    if (response.statistics) {
      let parcelStat = map(parcelStatistics, (statObj) => {
        statObj.count = response.statistics[statObj.key];
        return statObj;
      });

      setParcelStatistics(parcelStat);
    }
  };

  const getLocationList = async (selectedCustomers) => {
    const responseData = await fetchCustomerLocationList(
      { tempDelete: false },
      [selectedCustomers],
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "orgName", mode: SortingMode.DESC },
    );

    if (responseData && !responseData.error) {
      setLocationList(responseData);
      handleFetchServicePoints({ type: "STATION", active: true });
    } else {
      if (typeof responseData.error?.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  const getLocationListForModal = async (selectedCustomers: any[]) => {
    const responseData = await fetchCustomerLocationList(
      { tempDelete: false },
      selectedCustomers,
    );

    if (responseData && !responseData.error) {
      return responseData
    } else {
      if (typeof responseData.error?.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  const getWorkingHours = async (selectedLocation) => {
    const responseData = await fetchCustomerLocation(selectedLocation);

    if (responseData && !responseData.error) {
      setWorkingHours(responseData.workingHours);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleOpenParcelOrderProcessing = (orderID) => {
    const params = isArchive ? "list=archives" : "";
    routeToOrderView(orderID, location, history, params);
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handlePickUpDropOffSubmitParcels = async (type, data) => {
    if (type === "pickup") {
      const responseData = await createPickUp(data);
      if (responseData && !responseData.error) {
        toaster("success", t("toaster.createPickUpSuccess"));
        handleRefreshData();
      } else {
        if (typeof responseData.error?.message === "string") {
          toaster("error", responseData.error.message);
        } else {
          toaster("error", responseData.error.message.message.replace('"', ""));
        }
      }
    } else if (type === "dropoff") {
      const responseData = await createDropOff(data);
      if (responseData && !responseData.error) {
        toaster("success", t("toaster.createDropOffSuccess"));
        handleRefreshData();
      } else {
        if (typeof responseData.error?.message === "string") {
          toaster("error", responseData.error.message);
        } else {
          toaster("error", responseData.error.message.message.replace('"', ""));
        }
      }
    }
  };

  const handleStatusUpdate = async (
    status,
    orderIDs?,
    issueType?,
    remarks?,
  ) => {
    var responseData;
    if (remarks) {
      responseData = await updateOrderStatus(
        orderIDs,
        status,
        issueType,
        remarks,
      );
    } else {
      responseData = await updateOrderStatus(orderIDs, status, issueType);
    }

    if (!isNil(responseData) && responseData.updated) {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "success" },
        list: responseData,
      });
      handleTabClick(tabActiveKey);
      toaster("success", "Order Status updated successfully");
      handleRefreshData();
    } else {
      setOrderState({
        ...orderState,
        status: { ...orderState.status, fetch: "error" },
      });
      toaster("error", "Failed to update");
    }
  };

  const handleDownloadAirwayBill = async (orderID) => {
    try {
      const response = await getAirwayBills(orderID);
      if (response.updated && response.airwayBills.length) {
        const downloadUrls = response.airwayBills.map((url, index) => {
          return () => window.open(url);
        });

        downloadUrls.forEach((download, index) => {
          setTimeout(() => {
            download();
          }, index * 100);
        });

        setAllOrderList((prevState) => {
          return prevState.map((order, index) => {
            if (
              orderID[index] === order?._id &&
              !order?.airwayBill?.downloaded
            ) {
              return {
                ...order,
                airwayBill: { ...order?.airwayBill, downloaded: true },
              };
            }
            return order;
          });
        });
      } else {
        toaster("error", response.error.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const baseColumns: any = [
    {
      title: t("label.orderNo").toLocaleUpperCase(),
      dataIndex: "orderCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.orderCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenParcelOrderProcessing(record._id);
          },
        };
      },
    },
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 150,
      ellipsis: true,
    },
    {
      title: t("label.sender").toLocaleUpperCase(),
      dataIndex: ["individualName"],
      width: 100,
      ellipsis: true,
    },
    {
      title: t("txt.receiver").toLocaleUpperCase(),
      dataIndex: ["receiverInfo", "fullName"],
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.orderDate").toLocaleUpperCase(),
      dataIndex: "orderDate",
      width: 105,
      ellipsis: true,
      sorter: (a, b) =>
        a.createdAt > b.createdAt ? 1 : a.createdAt < b.createdAt ? -1 : 0,
      render: (_: any, record: any) => {
        return (
          <span>{moment(record?.createdAt).format("DD-MM-YYYY") || null}</span>
        );
      },
    },
    {
      title: t("label.deliveryDate").toLocaleUpperCase(),
      dataIndex: "deliveryDate",
      width: 105,
      ellipsis: true,
      sorter: (a, b) =>
        a.deliveryDate > b.deliveryDate
          ? 1
          : a.deliveryDate < b.deliveryDate
          ? -1
          : 0,
      render: (_: any, record: any) => {
        return (
          <span>
            {record?.deliveryDate
              ? moment(record?.deliveryDate).format("DD-MM-YYYY")
              : null}
          </span>
        );
      },
    },
  ];

  const baseColumns2: any = [
    {
      title: t("label.orderNo").toLocaleUpperCase(),
      dataIndex: "orderNum",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.orderCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenParcelOrderProcessing(record._id);
          },
        };
      },
    },
  ];

  const tabs = [
    {
      tabKey: "0",
      label: t("tab.newOrders"),
      component: (
        <OrderTab
          baseColumns={baseColumns}
          handleRefreshData={handleRefreshData}
          handleModalAction={handleModalAction}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("tab.pendingConfirmation"),
      component: (
        <PendingConfirmationTab
          baseColumns={baseColumns}
          handleRefreshData={handleRefreshData}
          handleModalAction={handleModalAction}
        />
      ),
    },
    {
      tabKey: "2",
      label: t("tab.pendingReview"),
      component: (
        <PendingReviewTab
          baseColumns={baseColumns}
          handleRefreshData={handleRefreshData}
          handleModalAction={handleModalAction}
        />
      ),
    },
    {
      tabKey: "3",
      label: t("tab.dispatchReady"),
      component: (
        <DispatchReadyTab
          getWorkingHours={getWorkingHours}
          baseColumns={baseColumns}
          handleRefreshData={handleRefreshData}
          handleFetchServicePoints={handleFetchServicePoints}
          getLocationList={getLocationList}
          getLocationListForModal={getLocationListForModal}
          onSubmit={handlePickUpDropOffSubmitParcels}
        />
      ),
    },
    {
      tabKey: "4",
      label: t("tab.enroute"),
      component: (
        <EnrouteTab
          baseColumns2={baseColumns2}
          handleDownloadAirwayBill={handleDownloadAirwayBill}
          handleRefreshData={handleRefreshData}
        />
      ),
    },
    {
      tabKey: "5",
      label: t("tab.rejectedOrders"),
      component: (
        <RejectedOrdersTab
          baseColumns2={baseColumns2}
          handleRefreshData={handleRefreshData}
          allArchiveObjects={allArchiveObjects}
          handleModalAction={handleModalAction}
        />
      ),
    },
    {
      tabKey: "6",
      label: t("tab.cancelledOrders"),
      component: (
        <CancelledOrdersTab
          baseColumns2={baseColumns2}
          handleRefreshData={handleRefreshData}
          allArchiveObjects={allArchiveObjects}
          handleModalAction={handleModalAction}
        />
      ),
    },
  ];

  const handleCreateParcelOrderAction = () => {
    history.push("/parcels-order-processing/parcels/create");
  };

  const handleUploadBulkOrderAction = () => {
    history.push("/parcels-order-processing/parcels/upload-mass-order");
  };

  const handleTabClick = (key) => {
    setSelectedRows([]);
    setFilterObj({
      ...initialFilters,
      orderStatus: tabStatus[key],
      orgID: initialFilters.orgID,
    });

    let path = "";

    if (key === "0") {
      path = page
        ? `/parcels-order-processing/parcels?page=${page}`
        : `/parcels-order-processing/parcels`;
    } else {
      path = page
        ? `/parcels-order-processing/parcels?tabKey=${key}&page=${page}`
        : `/parcels-order-processing/parcels?tabKey=${key}`;
    }

    setTabActiveKey(key);

    setIsArchive && setIsArchive(false);
    history.push(path);
  };

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      setFilterObj({
        ...initialFilters,
        ancestorOrgIDs: globalFilterState?.selectedOrgIDs,
      });
      handleFetchCustomers();
      getStatCount();
    }
  }, [globalFilterState]);

  useEffect(() => {
    setTimeout(getStatCount, 3000)
    handleFetchCustomers();
  }, []);

  useEffect(() => {
    isArchive ? handleListArchivedOrders() : fetchOrders();
  }, [filterObj, itemPerPage, queryParams]);

  return (
    <div className="parcel-order-listing">
      <Helmet>
        <title>{t("pageTitle.orderProcessing")}</title>
      </Helmet>

      <ModuleTitleBar titleText={t("title.parcelOrderProcessing")} />

      <>
        <Row className="mt-20">
          {/* STATISTICS BOXES */}
          <Col xs={24} sm={12} md={16} lg={18} xl={20}>
            <div className="parcel-statistics-group">
              {parcelStatistics.map((parcelStat, index) => (
                <StatisticBox
                  key={index}
                  statObj={parcelStat}
                  count={parcelStat.count}
                  context={"Parcel"}
                  onClick={handleTabClick}
                  icon={parcelStat.icon}
                  iconSrc={parcelStat.iconSrc}
                />
              ))}
            </div>
          </Col>

          {showCreateOption ? (
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <Row justify="end">
                <div className="action-create-order">
                  <Button
                    label={t("action.createOrder")}
                    icon={<RightCircleOutlined />}
                    type="primary"
                    size="large"
                    onClick={() => {
                      handleCreateParcelOrderAction();
                    }}
                  />
                </div>
                <div className="action-bulk-order">
                  <Button
                    label={t("action.uploadBulkOrder")}
                    icon={<RightCircleOutlined />}
                    type="default"
                    size="large"
                    onClick={() => {
                      handleUploadBulkOrderAction();
                    }}
                  />
                </div>
              </Row>
            </Col>
          ) : null}
        </Row>

        <Tabs
          destroyInactiveTabPane={true}
          onTabClick={handleTabClick}
          defaultActiveKey="0"
          activeKey={tabActiveKey}
          tabBarGutter={25}
          tabs={tabs}
        />
        <div className="my-30">{paginationComponent}</div>
      </>

      <ConfirmRejectModal
        callModal={callModal}
        modal={modal}
        handleResetModalState={handleResetModalState}
      />
    </div>
  );
};

const ParcelsOrder: React.FC = () => {
  return (
    <OrderListingProvider>
      <ParcelsOrderInner></ParcelsOrderInner>
    </OrderListingProvider>
  );
};

export default ParcelsOrder;
